<script setup>
import { VNodeRenderer } from '@layouts/components/VNodeRenderer';
import { themeConfig } from '@themeConfig';
import { logUserActivity, logViewSize } from '@/flagler-api/backend-api.js';
import { useLoginStore } from '@/stores/LoginStore';
import { useFeatureStore } from '@/stores/FeatureStore';
import { usePreferencesStore } from '@/stores/PreferencesStore';
import flaglerIllustration from '@/assets/images/illustrations/flagler-illustration-transparent.png';
import { camelCaseToWords } from '@/utils/camelCaseToWords';
import { toastInfo } from '@/utils/toast';
const form = ref({
  email: '',
  password: '',
  remember: false,
});

const isLoggedOutNotificationVisible = ref(false);
const invalidLogin = ref(false);
const invalidCompany = ref(false);

const isPasswordVisible = ref(false);
const route = useRoute();
const router = useRouter();
const loginStore = useLoginStore();
const preferencesStore = usePreferencesStore();

const login = async () => {
  const featureStore = useFeatureStore();
  const redirect = false; // localStorage.getItem('redirect');

  loginStore
    .login(form.value.email, form.value.password)
    .then(() => {
      logUserActivity('Login');
      logViewSize();

      if (route.query.to && route.query.to.length > 0) {
        router.replace(String(route.query.to));
      } else if (loginStore.redirectToRoute) {
        router.replace(loginStore.redirectToRoute);
      } else if (preferencesStore.redirectToPath) {
        router.replace(preferencesStore.redirectToPath);
      } else if (!redirect) {
        router.replace(featureStore.getHomeRoute());
      }
    })
    .catch((error) => {
      if (
        error.response?.data?.error.endsWith(
          'does not have a company association'
        )
      ) {
        invalidCompany.value = true;
      } else if (error.code === 401) {
        invalidLogin.value = true;
      }
    });
};

const refVForm = ref();
const onSubmit = () => {
  refVForm.value?.validate().then(({ valid: isValid }) => {
    if (isValid) login();
  });
};

onMounted(() => {
  if (window.location.href.includes('loggedOut=true')) {
    isLoggedOutNotificationVisible.value = true;
  }
});

const loginWithMicrosoft = () => {
  let url = import.meta.env.VITE_BACKEND_API_URL + '/auth/microsoft/signin';
  if (loginStore.redirectToRoute) {
    url = url + `?to=${encodeURIComponent(loginStore.redirectToRoute)}`;
  }
  window.location.href = url;
};

const loginWithGoogle = () => {
  const port = window.location.port ? ':' + window.location.port : '';
  const frontendUrl = `${window.location.protocol}//${window.location.hostname}${port}`;
  let url = `${import.meta.env.VITE_BACKEND_API_URL}/auth/google/signin?clientUrl=${frontendUrl}`;
  if (loginStore.redirectToRoute) {
    url = url + `&to=${encodeURIComponent(loginStore.redirectToRoute)}`;
  }
  window.location.href = url;
};

const version = () => {
  return import.meta.env.VITE_APP_VERSION || 'Unknown';
};

const allVerison = () => {
  if (import.meta.env.VITE_ENVIRONMENT === 'prod') {
    return {
      environment: import.meta.env.VITE_ENVIRONMENT,
      version: import.meta.env.VITE_APP_VERSION,
      buildDate: import.meta.env.VITE_APP_BUILD_DATE,
    };
  } else {
    return {
      environment: import.meta.env.VITE_ENVIRONMENT,
      version: import.meta.env.VITE_APP_VERSION,
      branch: import.meta.env.VITE_APP_BRANCH,
      buildDate: import.meta.env.VITE_APP_BUILD_DATE,
      commitHash: import.meta.env.VITE_APP_COMMIT,
      uncommittedChanges: import.meta.env.VITE_APP_DIRTY,
    };
  }
};

const konamiCode = [38, 38, 40, 40, 37, 39, 37, 39, 66, 65];
let konamiPosition = 0;

document.addEventListener('keydown', (e) => {
  if (e.keyCode === konamiCode[konamiPosition]) {
    konamiPosition++;
    if (konamiPosition === konamiCode.length) {
      versionToast();
      konamiPosition = 0;
    }
  } else {
    konamiPosition = 0;
  }
});

function versionToast() {
  let versionInfo = allVerison();
  let content = Object.entries(versionInfo)
    .map(([key, value]) => {
      return `${camelCaseToWords(key)}: <b>${value}</b>`;
    })
    .join('<br />');
  toastInfo('Version Info', content, undefined, 15000);
}
</script>

<template>
  <VRow no-gutters class="auth-wrapper">
    <VSnackbar v-model="isLoggedOutNotificationVisible" location="top" :timeout="6000000000">
      You've been logged out due to inactivity.
    </VSnackbar>
    <VCol md="8" class="d-none d-md-flex">
      <div class="position-relative auth-bg rounded-lg w-100 ma-8 me-0">
        <div class="d-flex align-center justify-center w-100 h-100">
          <VImg max-width="505" :src="flaglerIllustration" class="auth-illustration mt-16 mb-2" />
        </div>
      </div>
    </VCol>

    <VCol
      cols="12"
      md="4"
      class="auth-card-v2 d-flex align-center justify-center"
      style="flex-direction: column"
    >
      <div
        style="
          flex-grow: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
        "
      >
        <VCard flat :max-width="500" class="mt-12 mt-sm-0 pa-4">
          <VCardText>
            <VNodeRenderer :nodes="themeConfig.app.logo" class="mb-6 default-logo-size" />
            <h5 class="text-h5 font-weight-semibold mb-1">
              Welcome to Flagler Health! 👋🏻
            </h5>
            <p class="mb-0">
              Please sign in to your account below.
              <br>
            </p>
          </VCardText>
          <VCardText>
            <VBtn variant="outlined" class="login" style="" @click="loginWithMicrosoft">
              <template #prepend>
                <img src="@/assets/microsoft_logo.svg">
              </template>
              Continue with Microsoft
            </VBtn>
          </VCardText>
          <VCardText>
            <VBtn variant="outlined" class="login" style="" @click="loginWithGoogle">
              <template #prepend>
                <img src="@/assets/google_logo.svg">
              </template>
              Continue with Google
            </VBtn>
          </VCardText>
          <VCol cols="12" class="d-flex align-center">
            <VDivider />
            <span class="mx-4">or</span>
            <VDivider />
          </VCol>
          <VCardText>
            <p v-if="invalidLogin" style="color: #ec6178">
              Sorry, your email or password was not recognized.
            </p>
            <p v-else-if="invalidCompany" style="color: #ec6178">
              Account setup is incomplete and does not have any customer
              association. Please contact support.
            </p>
            <VForm ref="refVForm" @submit.prevent="onSubmit">
              <VRow>
                <!-- email -->
                <VCol cols="12">
                  <VTextField id="email-login" v-model="form.email" label="Email" type="email" />
                </VCol>

                <!-- password -->
                <VCol cols="12">
                  <VTextField
                    id="password-login"
                    v-model="form.password"
                    label="Password"
                    :type="isPasswordVisible ? 'text' : 'password'"
                    :append-inner-icon="isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'"
                    @click:append-inner="isPasswordVisible = !isPasswordVisible"
                  />

                  <div class="d-flex align-center flex-wrap justify-space-between mt-2 mb-4">
                    <RouterLink class="text-primary ms-2 mb-1" :to="{ name: 'forgot-password' }">
                      Forgot Password?
                    </RouterLink>
                  </div>

                  <VBtn id="login-submit-button" block type="submit">
                    Login
                  </VBtn>

                  <!--                 <div class="d-flex align-center mt-2 mb-4">
                  <VLabel
                    for="privacy-policy"
                    class="pb-1"
                    style="opacity: 1; white-space: normal"
                  >
                    <span class="me-1 text-subtitle-2"
                      >By continuing, you agree to the
                      <a
                        href="https://www.flaglerhealth.io/privacy"
                        target="_blank"
                        class="text-primary"
                        >privacy policy</a
                      >
                      and
                      <a
                        href="https://www.flaglerhealth.io/term-of-use"
                        target="_blank"
                        class="text-primary"
                        >terms of use</a
                      ></span
                    >
                  </VLabel>
                </div>
 -->
                </VCol>
              </VRow>
            </VForm>
          </VCardText>
        </VCard>
      </div>
      <VLabel for="version-number" class="pb-1 mb-2" style="opacity: 1; white-space: normal">
        <span
          class="me-1 text-subtitle-2"
          style="opacity: 0.33"
          :onClick="() => {
            versionToast();
          }
          "
        >
          Version {{ version() }}
        </span>
      </VLabel>
      <VLabel for="privacy-policy" class="pb-1 mb-8" style="opacity: 1; white-space: normal">
        <span class="me-1 text-subtitle-2">
          By continuing, you agree to the
          <a href="https://www.flaglerhealth.io/privacy" target="_blank" class="text-primary">
            privacy policy
          </a>
          and
          <a href="https://www.flaglerhealth.io/term-of-use" target="_blank" class="text-primary">
            terms of use
          </a>
        </span>
      </VLabel>
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use '@core/scss/template/pages/page-auth.scss';

.v-btn.login {
  width: 100%;
  grid-template-columns: max-content 1fr max-content;
  --v-theme-primary: 94, 94, 94;
  font-family: 'Segoe UI', sans-serif;
  font-size: 15px !important;
  border-color: #d1cfd5;
}
</style>

<route lang="yaml">
meta:
  layout: blank
  action: read
  subject: VueAuth
  redirectIfLoggedIn: true
  title: Login
</route>
